import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnFeedQueryParameters,
  ISnListQueryParameters,
  ISnListResponse,
  transformListResponse,
} from '@lib/api';
import { SnShare, SnTag, SnTagRecord } from '@lib/api/entities';
import { ISnCanShare } from '@lib/share';
import { t } from '@lib/translation/helpers/translation.helper';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnTagsAPI implements ISnCanShare {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _dialogService: SnDialogService,
    private readonly _toastrService: ToastrService,
  ) {}

  public getTagList(params: ISnListQueryParameters = {}): Observable<ISnListResponse<SnTag>> {
    return this._httpClient
      .get('/api/tags/', { params })
      .pipe(map((response: any) => transformListResponse(response, (item) => new SnTag(item))));
  }

  public feedTags(params: ISnFeedQueryParameters): Observable<SnTag[]> {
    return this._httpClient
      .get('/api/tags/feed/', { params })
      .pipe(map((response: any) => response.map((item) => new SnTag(item))));
  }

  public getTag(id: string): Observable<SnTag> {
    return this._httpClient
      .get(`/api/tags/${id}/`)
      .pipe(map((response: Object) => new SnTag(response)));
  }

  public createTag(data: object): Observable<SnTag> {
    return this._httpClient
      .post('/api/tags/', data)
      .pipe(map((response: Object) => new SnTag(response)));
  }

  public updateTag(id: string, data: object): Observable<SnTag> {
    return this._httpClient
      .put(`/api/tags/${id}/`, data)
      .pipe(map((response: Object) => new SnTag(response)));
  }

  public deleteTag(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('tags.organization.deleteConfirm'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/tags/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('tags.organization.deleted')),
          error: () => this._toastrService.error(t('tags.organization.deleteError')),
        }),
      );
  }

  public getTagRecordList(
    tagId: string,
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnTagRecord>> {
    return this._httpClient.get(`/api/tags/${tagId}/records/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((item) => new SnTagRecord(item)),
      })),
    );
  }

  public getShareList(
    tagId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/tags/${tagId}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(tagId: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/tags/${tagId}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(tagId: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/tags/${tagId}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(tagId: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('shares.confirmDeleteShare'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/tags/${tagId}/shares/${shareId}/`);
        }),
      );
  }
}
